<template>
  <div class="tools-toolbar d-flex flex-column">
    <div class="tools-toolbar-main d-inline-flex align-center flex-wrap">
      <!-- ============ FILTER BUTTON + POPOVER ============ -->
      <v-menu
        v-if="!hideFilter"
        v-model="filterMenuOpen"
        :close-on-content-click="false"
        content-class="tools-toolbar-menu"
        offset-y
        nudge-bottom="6"
        min-width="360"
        max-width="420"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            ref="filterActivator"
            class="text-none toolbar-btn elevation-0 rounded-r-0 rounded-l-xl"
            :color="themeButtonColor({ active: filterCount > 0, activeDark: 'darkestblue' })"
            v-bind="attrs"
            v-on="on"
          >
            <font-awesome-icon
              :icon="['fas', 'filter']"
              size="sm"
              :class="{ 'mr-1': $vuetify.breakpoint.lgAndUp }"
            />

            <span class="d-none d-lg-inline">Filter</span>

            <v-badge
              v-if="filterCount > 0"
              :content="filterCount"
              color="primary"
            />
          </v-btn>
        </template>

        <v-card
          ref="filterPopover"
          class="tools-popover"
          @keydown.esc.stop.prevent="closeMenuAndRestoreFocus('filter')"
        >
          <div class="tools-popover-header py-3 px-4 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'filter']"
              size="sm"
              class="mr-2"
            />
            <span class="subtitle-2 font-weight-medium">Filter</span>

            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              size="sm"
              class="ml-auto grey--text cursor-pointer"
              @click="showHelpForFiltering = !showHelpForFiltering"
            />
          </div>

          <v-divider v-if="showHelpForFiltering" />

          <v-row v-if="showHelpForFiltering">
            <v-col>
              <div class="ma-4 caption font-weight-medium">
                Select values to filter the data by that criterion.
              </div>
            </v-col>
          </v-row>

          <v-divider />

          <div class="tools-popover-content pa-4">
            <!-- View Context Label -->
            <div
              v-if="viewContext"
              class="caption grey--text mb-3"
            >
              Filtering: {{ viewContext }}
            </div>

            <!-- Active Saved Filter Chip -->
            <v-chip
              v-if="activeFilterName"
              color="primary"
              outlined
              close
              class="mb-3"
              @click:close="clearActiveFilter"
            >
              <font-awesome-icon
                :icon="['fas', 'bookmark']"
                class="mr-2"
                size="sm"
              />
              {{ activeFilterName }}
            </v-chip>

            <!-- Preview Results Alert -->
            <v-alert
              v-if="showPreview"
              type="info"
              text
              dense
              class="mb-3"
            >
              <div class="d-flex align-center">
                <font-awesome-icon
                  :icon="['fas', 'eye']"
                  class="mr-2"
                />
                <span>Preview: {{ previewCount }} {{ previewCount === 1 ? 'result' : 'results' }}</span>
              </div>
            </v-alert>

            <!-- Filter Controls (slot for parent views to provide their own filter UI) -->
            <slot name="filter-controls">
              <delivery-filter-form
                v-if="isDeliveryRoute"
                :filter-options="computedFilterOptions"
              />
              <internal-filter-form v-if="isInternalRoute" />
              <programmatic-filter-form v-if="isProgrammaticRoute" />
            </slot>

            <!-- Saved Filters Section -->
            <div
              v-if="savedFiltersViewName"
              class="mt-4"
            >
              <v-divider class="mb-4" />
              <div class="d-flex align-center mb-2">
                <font-awesome-icon
                  :icon="['fas', 'bookmark']"
                  class="mr-2 primary--text"
                  size="sm"
                />
                <span class="font-weight-bold">Saved Filters</span>
              </div>
              <saved-filters-menu
                :view-name="savedFiltersViewName"
                :current-filters="currentFilters"
              />
            </div>
          </div>

          <v-divider />

          <v-card-actions class="pa-3">
            <v-btn
              text
              small
              color="error"
              class="text-none"
              :loading="resettingFilter"
              @click="resetFilters"
            >
              Reset
            </v-btn>
            <v-spacer />
            <v-btn
              small
              depressed
              color="primary"
              class="text-none"
              :loading="applyingFilter"
              @click="applyFilters"
            >
              Apply
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>

      <!-- ============ Group Button + Popover ============ -->
      <!-- Group Button -->
      <v-menu
        v-if="!hideGroup"
        v-model="groupMenuOpen"
        :close-on-content-click="false"
        content-class="tools-toolbar-menu"
        offset-y
        nudge-bottom="6"
        min-width="320"
        max-width="320"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            ref="groupActivator"
            class="text-none toolbar-btn elevation-0 rounded-0"
            :color="themeButtonColor({ active: groupCount > 0 })"
            v-bind="attrs"
            v-on="on"
          >
            <font-awesome-icon
              :icon="['fas', 'layer-group']"
              size="sm"
              :class="{ 'mr-1': $vuetify.breakpoint.lgAndUp }"
            />
            <span class="d-none d-lg-inline">Group</span>
            <v-badge
              v-if="groupCount > 0"
              :content="groupCount"
              color="primary"
              inline
              class="ml-1"
            />
          </v-btn>
        </template>

        <v-card
          ref="groupPopover"
          class="tools-popover"
          @keydown.esc.stop.prevent="closeMenuAndRestoreFocus('group')"
        >
          <div class="tools-popover-header py-3 px-4 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'layer-group']"
              size="sm"
              class="mr-2"
            />
            <span class="subtitle-2 font-weight-medium">Group</span>

            <v-spacer />

            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              size="sm"
              class="grey--text cursor-pointer"
              @click="showHelpForGrouping = !showHelpForGrouping"
            />
          </div>

          <v-divider v-if="showHelpForGrouping" />

          <div
            v-if="showHelpForGrouping"
            class="ma-3 caption font-weight-medium"
            style="line-height: 1.3;"
          >
            Add grouping columns in hierarchical order, from broadest to most specific.
          </div>

          <v-divider />

          <div class="tools-popover-content pa-4">
            <!-- Active group chips (draggable order) -->
            <div
              v-for="(groupKey, index) in localGroupedBy"
              :key="`group-row-${groupKey}`"
              class="group-row d-flex align-center justify-space-between mb-2 pa-2 rounded"
            >
              <div class="d-flex align-center flex-grow-1">
                <!-- Editing mode: autocomplete to replace group -->
                <v-autocomplete
                  v-if="editingGroupRowIndex === index"
                  :value="groupKey"
                  :items="getGroupOptions(index)"
                  :placeholder="groupOptionLabel(groupKey)"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  autofocus
                  @change="saveEditedGroupRow(index, $event)"
                  @blur="editingGroupRowIndex = null"
                  @keydown.esc="editingGroupRowIndex = null"
                />
                <!-- Display mode: label with click handler -->
                <div
                  v-else
                  class="group-label-row d-flex flex-grow-1 align-center cursor-pointer"
                  data-elementName="SelectedGroupOptionLabel"
                  @click="editingGroupRowIndex = index"
                >
                  <span class="caption font-weight-medium mr-2 grey--text">{{ index + 1 }}</span>
                  <span class="body-2 font-weight-medium flex-grow-1">{{ groupOptionLabel(groupKey) }}</span>
                  <font-awesome-icon
                    :icon="['fas', 'pencil']"
                    size="xs"
                    class="ml-2 grey--text group-edit-icon"
                  />
                </div>
              </div>

              <v-btn
                icon
                x-small
                @click="removeGroupRow(index)"
              >
                <font-awesome-icon
                  :icon="['fas', 'xmark']"
                  size="sm"
                  class="grey--text"
                />
              </v-btn>
            </div>

            <!-- Empty state -->
            <div
              v-if="localGroupedBy.length === 0 && availableGroupOptions.length === 0"
              class="caption grey--text text-center py-2"
            >
              No grouping applied
            </div>

            <!-- Add group column -->
            <div
              v-if="availableGroupOptions.length > 0"
              class="mt-3"
            >
              <v-autocomplete
                v-model="pendingGroupColumn"
                placeholder="Add a grouping column"
                :items="availableGroupOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                clearable
                @change="onGroupColumnSelected"
              >
                <template v-slot:prepend-inner>
                  <font-awesome-icon
                    :icon="['fas', 'plus']"
                    size="sm"
                    class="grey--text mt-1 mr-1"
                  />
                </template>
              </v-autocomplete>
            </div>
          </div>

          <v-divider />

          <v-card-actions class="pa-3">
            <v-btn
              text
              small
              color="error"
              class="text-none"
              @click="clearGrouping"
            >
              Clear
            </v-btn>
            <v-spacer />
            <v-btn
              small
              depressed
              color="primary"
              class="text-none"
              @click="applyGrouping"
            >
              Apply
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>

      <!-- ============ Sort Button + Popover ============ -->
      <v-menu
        v-if="!hideSort"
        v-model="sortMenuOpen"
        :close-on-content-click="false"
        content-class="tools-toolbar-menu"
        offset-y
        nudge-bottom="6"
        min-width="372"
        max-width="372"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            ref="sortActivator"
            class="text-none toolbar-btn elevation-0 rounded-0"
            :color="themeButtonColor({ active: sortCount > 0 })"
            v-bind="attrs"
            v-on="on"
          >
            <font-awesome-icon
              :icon="['fas', 'arrow-down-short-wide']"
              size="sm"
              :class="{ 'mr-1': $vuetify.breakpoint.lgAndUp }"
            />
            <span class="d-none d-lg-inline">Sort</span>
            <v-badge
              v-if="sortCount > 0"
              :content="sortCount"
              color="primary"
              inline
              class="ml-1"
            />
          </v-btn>
        </template>

        <v-card
          ref="sortPopover"
          class="tools-popover"
          @keydown.esc.stop.prevent="closeMenuAndRestoreFocus('sort')"
        >
          <div class="tools-popover-header py-3 px-4 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'arrow-down-short-wide']"
              size="sm"
              class="mr-2"
            />
            <span class="subtitle-2 font-weight-medium">Sort</span>

            <v-spacer />

            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              size="sm"
              class="grey--text cursor-pointer"
              @click="showHelpForSort = !showHelpForSort"
            />
          </div>

          <v-divider v-if="showHelpForSort" />

          <div
            v-if="showHelpForSort"
            class="ma-3 caption font-weight-medium"
            style="line-height: 1.3;"
          >
            Select sort columns in hierarchical order.
            Toggle the direction button for ascending or descending.
          </div>

          <v-divider />

          <div class="tools-popover-content pa-4">
            <!-- Dynamic Sort Rows -->
            <div
              v-for="(sort, index) in localSorts"
              :key="`sort-row-${index}`"
              class="sort-row mb-3"
            >
              <div class="d-flex align-center mb-1">
                <span class="caption font-weight-medium">{{ sortRowLabel(index) }}</span>
                <v-spacer />
                <v-btn
                  v-if="index > 0"
                  icon
                  x-small
                  class="remove-sort-btn"
                  @click="removeSortRow(index)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'xmark']"
                    size="sm"
                    class="grey--text"
                  />
                </v-btn>
              </div>

              <div class="d-flex align-center">
                <v-autocomplete
                  v-model="sort.column"
                  :placeholder="index === 0 ? 'Select column' : 'Select column'"
                  :items="availableSortColumns(index)"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  clearable
                  class="flex-grow-1"
                />

                <v-btn
                  outlined
                  :disabled="!sort.column"
                  :color="sort.column ? 'default' : 'actiongray'"
                  class="text-none ml-2 sort-dir-btn"
                  @click="toggleSortDirection(index)"
                >
                  <font-awesome-icon
                    :icon="sort.direction === 'asc' ? ['fas', 'arrow-down-short-wide'] : ['fas', 'arrow-down-wide-short']"
                    class="mr-1"
                  />
                  <span>{{ sort.direction === 'asc' ? 'A → Z' : 'Z → A' }}</span>
                </v-btn>
              </div>
            </div>

            <v-row>
              <v-col>
                <!-- Add Sort Button -->
                <v-btn
                  :disabled="!canAddSortRow"
                  outlined
                  small
                  color="primary"
                  class="text-none mt-1"
                  @click="addSortRow"
                >
                  <font-awesome-icon
                    :icon="['fas', 'plus']"
                    size="sm"
                    class="mr-1"
                  />
                  <span>Then By</span>
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <v-divider />

          <v-card-actions class="pa-3">
            <v-btn
              text
              small
              color="error"
              class="text-none"
              :disabled="localSorts.length === 1 && !localSorts[0].column"
              @click="clearSorting"
            >
              Clear
            </v-btn>
            <v-spacer />
            <v-btn
              small
              depressed
              color="primary"
              class="text-none"
              @click="applySorting"
            >
              Apply
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>

      <!-- ============ Column Picker and Menu ============ -->
      <v-menu
        v-if="!hideColumns"
        v-model="columnsMenuOpen"
        :close-on-content-click="false"
        content-class="tools-toolbar-menu"
        offset-y
        nudge-bottom="6"
        min-width="340"
        max-width="450"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            ref="columnsActivator"
            class="text-none toolbar-btn elevation-0 rounded-r-xl rounded-l-0"
            :color="themeButtonColor()"
            v-bind="attrs"
            v-on="on"
          >
            <font-awesome-icon
              :icon="['fas', 'table-columns']"
              size="sm"
              :class="{ 'mr-1': $vuetify.breakpoint.lgAndUp }"
            />
            <span class="d-none d-lg-inline">Columns</span>
          </v-btn>
        </template>

        <!-- Columns Popover -->
        <v-card
          ref="columnsPopover"
          class="tools-popover"
          @keydown.esc.stop.prevent="closeMenuAndRestoreFocus('columns')"
        >
          <div class="tools-popover-header py-3 px-4 d-flex align-center">
            <font-awesome-icon
              :icon="['fas', 'table-columns']"
              size="sm"
              class="mr-2"
            />
            <span class="subtitle-2 font-weight-medium">Columns</span>

            <v-spacer />

            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              size="sm"
              class="grey--text cursor-pointer"
              @click.stop="showHelpForColumns = !showHelpForColumns"
            />
          </div>

          <v-divider />

          <div class="tools-popover-content pa-4 columns-panel">
            <!-- View Context Label -->
            <div
              v-if="viewContext"
              class="caption grey--text mb-3"
            >
              Columns: {{ viewContext }}
            </div>

            <div
              v-if="showHelpForColumns"
              class="caption mb-3 px-4"
            >
              <div class="d-inline-block">
                <font-awesome-icon
                  :icon="['fas', 'grip-dots-vertical']"
                  class="mr-1"
                />
                <span class="font-weight-medium">Drag to reorder</span>
              </div>
              <span class="mx-2">|</span>
              <div class="d-inline-block">
                <font-awesome-icon
                  :icon="['fas', 'thumbtack']"
                  class="mr-1"
                />
                <span class="font-weight-medium">Pin to freeze</span>
              </div>
              <span class="mx-2">|</span>
              <div class="d-inline-block">
                <font-awesome-icon
                  :icon="['fas', 'square-check']"
                  class="mr-1"
                />
                <span class="font-weight-medium">Toggle visibility</span>
              </div>
            </div>

            <v-divider v-if="showHelpForColumns" />

            <v-list
              dense
              class="columns-list"
            >
              <draggable v-model="orderedColumns">
                <v-list-item
                  v-for="(column, colIndex) of orderedColumns"
                  :key="column.key || colIndex"
                  dense
                  class="column-item"
                >
                  <v-list-item-icon class="mr-2 my-auto align-center">
                    <font-awesome-icon
                      :icon="['fas', 'grip-dots-vertical']"
                      class="cursor-grab grey--text"
                    />
                  </v-list-item-icon>

                  <v-list-item-icon class="mr-2 my-auto align-center">
                    <font-awesome-icon
                      :icon="['fas', 'thumbtack']"
                      :class="`cursor-pointer ${column.frozen ? 'primary--text' : 'grey--text'}`"
                      @click="freezeColumn(column)"
                    />
                  </v-list-item-icon>

                  <v-list-item-icon class="mr-2 my-auto align-center">
                    <font-awesome-icon
                      v-if="column.visible || column.required"
                      :icon="['fas', 'square-check']"
                      :class="`cursor-pointer ${column.visible && !column.required ? 'primary--text' : 'grey--text'}`"
                      @click="toggleColumn(column)"
                    />
                    <font-awesome-icon
                      v-else
                      :icon="['far', 'square']"
                      class="cursor-pointer grey--text"
                      @click="toggleColumn(column)"
                    />
                  </v-list-item-icon>

                  <v-list-item-content>
                    <div
                      v-if="shouldRenderColumnGroupHeader(colIndex)"
                      class="caption font-weight-bold primary--text mb-1"
                    >
                      {{ getColumnGroup(column) }}
                    </div>

                    <v-list-item-title class="body-2">
                      <span>{{ getColumnLabel(column) }}</span>

                      <span
                        v-if="column.required"
                        class="caption grey--text ml-1"
                      >(required)</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </draggable>
            </v-list>
          </div>
        </v-card>
      </v-menu>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import draggable from 'vuedraggable'

import render from 'src/utils/render'
import themeClasses from '@/mixins/themeClasses'
import columnUtils from '@/tools/store/utils/columns'

const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'ToolsToolbar',
  components: {
    draggable,
    DeliveryFilterForm: () => import('./DeliveryFilterForm.vue'),
    InternalFilterForm: () => import('./InternalFilterForm.vue'),
    ProgrammaticFilterForm: () => import('./ProgrammaticFilterForm.vue'),
    SavedFiltersMenu: () => import('./SavedFiltersMenu.vue'),
  },
  mixins: [themeClasses],
  props: {
    // Self-managed mode: all state is local, no tools store dependency.
    // Use this for dashboard views or any context where the tools store isn't configured.
    selfManaged: {
      type: Boolean,
      default: false,
    },
    // Hide the Filter button (for views with their own filter implementation)
    hideFilter: {
      type: Boolean,
      default: false,
    },
    // Hide the Columns button (for views with their own column drawer)
    hideColumns: {
      type: Boolean,
      default: false,
    },
    // Hide the Group button (for views with their own grouping implementation)
    hideGroup: {
      type: Boolean,
      default: false,
    },
    // Hide the Sort button (for views with their own sorting implementation)
    hideSort: {
      type: Boolean,
      default: false,
    },
    // Override active filter count for self-managed consumers using custom filter-controls slots
    filterCountOverride: {
      type: Number,
      default: null,
    },
    // Items array for computing filter options (for self-managed views)
    items: {
      type: Array,
      default: () => [],
    },
    // Sort options array [{text, value}] — overrides store-derived sortable columns
    sortOptions: {
      type: Array,
      default: () => [],
    },
    // Group options array [{text, value}] — overrides store-derived grouping options
    groupOptions: {
      type: Array,
      default: () => [],
    },

    // Column config
    currentRequiredColumns: {
      type: Array,
      default: () => [],
    },
    reportKey: {
      type: String,
      default: undefined,
    },
    tableColumns: {
      type: Array,
      default: () => [],
    },
    // ====== FILTER ENHANCEMENTS ======
    // View context label (e.g., "Time Entry", "MTD")
    viewContext: {
      type: String,
      default: '',
    },
    // Show preview count alert
    showPreview: {
      type: Boolean,
      default: false,
    },
    // Preview result count
    previewCount: {
      type: Number,
      default: 0,
    },
    // Active saved filter name
    activeFilterName: {
      type: String,
      default: '',
    },
    // View name for saved filters (e.g., 'time', 'pacing')
    savedFiltersViewName: {
      type: String,
      default: '',
    },
    // Current filters object for saved filters comparison
    currentFilters: {
      type: Object,
      default: () => ({}),
    },
  },
  /**
   * @method data
   * @desc Component local state
   */
  data() {
    return {
      // Help toggles
      showHelpForColumns: false,
      showHelpForFiltering: false,
      showHelpForGrouping: false,
      showHelpForSort: false,

      // Individual menu open states
      filterMenuOpen: false,
      groupMenuOpen: false,
      sortMenuOpen: false,
      columnsMenuOpen: false,

      // Local grouping state
      localGroupedBy: [],
      pendingGroupColumn: null,
      editingGroupRowIndex: null, // Track which group row is being edited

      // Local sorting state (dynamic array of sort objects)
      localSorts: [{ column: null, direction: 'asc' }],
      maxSortLevel: 3,
      // @see src/tools/components/table/DataTable.vue#
      sortIconAsc: ['fas', 'arrow-down-short-wide'],
      sortIconDesc: ['fas', 'arrow-down-wide-short'],
    }
  },
  computed: {
    ...mapToolsGetters([
      'currentColumns',
      'currentFilterData',
      'currentGroupByProperties',
      'currentGroupingOptions',
      'currentItems',
      'currentNumberOfAppliedFilters',
      'frozenColumns',
      'isDeliveryRoute',
      'isInternalRoute',
      'isProgrammaticRoute',
      'sortByDirections',
      'sortByKeys',
      'totalAppliedDataTools',
    ]),
    ...mapToolsState({
      applyingFilter: (state) => state.applyingFilter,
      resettingFilter: (state) => state.resettingFilter,
      filterData: (state) => state.filterData,
      filterDefaults: (state) => state.filterDefaults,
      reportId: (state) => state.reportId,
      reportName: (state) => state.reportName,
      subId: (state) => state.subId,
    }),

    // Self-managed mode: when selfManaged prop is set, or items prop is provided
    isSelfManaged() {
      return this.selfManaged || (this.items && this.items.length > 0)
    },

    // Filter count (actual applied filters only, not groups/merges)
    filterCount() {
      if (Number.isFinite(this.filterCountOverride)) return this.filterCountOverride
      if (this.selfManaged) return 0
      return this.currentNumberOfAppliedFilters || 0
    },

    haveLocalGrouping() {
      return Array.isArray(this.localGroupedBy) && this.localGroupedBy.length > 0
    },

    localGroupedByColumnNames() {
      return this.currentColumns
        .filter((col) => this.localGroupedBy.indexOf(col.key) > -1)
        .map((col) => col.columnName)
    },

    // Group count — use local state when self-managed
    groupCount() {
      if (this.selfManaged) {
        return (this.localGroupedBy || []).length
      }
      const storeGroups = this.currentGroupByProperties || []
      return storeGroups.length
    },

    // Sort count — use local state when self-managed
    sortCount() {
      if (this.selfManaged) {
        return this.localSorts.filter((s) => s.column).length
      }
      const sorts = this.sortByKeys || []
      return sorts.length
    },

    /**
     * Compute filter options from items array (for self-managed views)
     */
    computedFilterOptions() {
      if (!this.items || this.items.length === 0) return null

      const extractUniqueValues = (fieldName, operator = 'incl') => {
        const seen = new Set()
        const values = []

        this.items.forEach((item) => {
          const value = item[fieldName]
          if (value != null && value !== '' && !seen.has(value)) {
            seen.add(value)
            values.push({
              text: String(value),
              value: String(value),
              operator,
              columnValue: value,
            })
          }
        })

        return values.sort((a, b) => String(a.text).toLowerCase().localeCompare(String(b.text).toLowerCase()))
      }

      return {
        advertiserName: extractUniqueValues('advertiserName'),
        creativeSizes: extractUniqueValues('creativeSizes', 'eq'),
        customFields: extractUniqueValues('customFields'),
        discrepancy: extractUniqueValues('discrepancy'),
        gamPriority: extractUniqueValues('gamPriority'),
        indicator: extractUniqueValues('indicator'),
        lineItemName: extractUniqueValues('lineItemName'),
        orderName: extractUniqueValues('orderName'),
        osi: extractUniqueValues('osi'),
        priority: extractUniqueValues('priority'),
        salespersonName: extractUniqueValues('salespersonName'),
        secondaryTraffickerNames: extractUniqueValues('secondaryTraffickerNames'),
        status: extractUniqueValues('status'),
        traffickerName: extractUniqueValues('traffickerName'),
        topLevelAdUnitIds: extractUniqueValues('topLevelAdUnitIds', 'eq'),
        topLevelAdUnitNames: extractUniqueValues('topLevelAdUnitNames'),
      }
    },

    // Grouping options — prefer prop override, then derive from columns (in column order), fall back to store
    resolvedGroupingOptions() {
      // 1. Prop override takes priority
      if (this.groupOptions && this.groupOptions.length > 0) {
        return this.groupOptions
      }

      // 2. Derive from columns (maintains column order for consistency)
      if (this.currentColumns && this.currentColumns.length > 0) {
        return this.currentColumns
          .filter((col) => col.key !== 'actions')
          .map((col) => {
            let textLabel = columnUtils.getColumnLabel(col)
            if (typeof textLabel === 'object') {
              textLabel = textLabel.text || String(textLabel)
            }
            return {
              text: String(textLabel),
              value: col.key,
            }
          })
      }

      // 3. Fall back to store (for backward compatibility)
      return this.currentGroupingOptions || []
    },

    hasGroupSelectedAll() {
      const options = this.resolvedGroupingOptions || []
      const selected = this.localGroupedBy || []
      return options.length > 0 && selected.length === options.length
    },

    availableGroupOptions() {
      const selected = this.localGroupedBy || []
      return (this.resolvedGroupingOptions || []).filter((opt) => !selected.includes(opt.value))
    },

    // Sortable columns — prefer prop override, fall back to store-derived columns
    sortableColumns() {
      if (this.sortOptions && this.sortOptions.length > 0) {
        return this.sortOptions
      }

      if (!this.currentColumns) return []

      return this.currentColumns
        .filter((col) => col.key !== 'actions')
        .map((col) => {
          let textLabel = columnUtils.getColumnLabel(col)
          if (typeof textLabel === 'object') {
            textLabel = textLabel.text || String(textLabel)
          }

          return {
            text: String(textLabel),
            value: col.key,
          }
        })
        .sort((a, b) => {
          const textA = a.text || ''
          const textB = b.text || ''
          return textA.localeCompare(textB)
        })
    },

    canAddSortRow() {
      // Disallow adding more than max sort levels to prevent excessive
      // sorting complexity and performance issues.
      if (this.localSorts.length > this.maxSortLevel) return false

      // Disallow adding a new sort row if the last sort row is incomplete.
      const lastSort = this.localSorts[this.localSorts.length - 1]
      const haveLastSort = typeof lastSort === 'object' && lastSort !== null

      return haveLastSort && lastSort.column
    },

    // Column management

    /**
     * Get or set the ordered columns for the table.
     * @see src/tools/store/getters.js#reportColumns
     */
    orderedColumns: {
      get() {
        return columnUtils.normalizeColumns(this.tableColumns, this.currentRequiredColumns)
      },
      set(newOrder) {
        if (this.selfManaged) {
          this.$emit('on-columns-change', columnUtils.normalizeColumns(newOrder, this.currentRequiredColumns))
          return
        }
        this.$store.commit('tools/columns', { [this.reportKey]: newOrder })
      },
    },

    /**
     * Whether the column list actually spans more than one group. Most views (e.g. Time.vue's
     * MTD/Billable columns) never set a `group` on their column definitions, so every column
     * falls back to the same implicit 'Other' bucket - in that case there's nothing to label,
     * and shouldRenderColumnGroupHeader should never render a header at all.
     */
    hasMultipleColumnGroups() {
      const groups = new Set((this.orderedColumns || []).map((column) => this.getColumnGroup(column)))

      return groups.size > 1
    },
  },
  watch: {
    // Close all menus when reportKey changes (navigation between tabs/views)
    reportKey() {
      this.closeAllMenus()
    },

    filterMenuOpen(isOpen) {
      this.handleMenuOpenChange({ isOpen, popoverRef: 'filterPopover', activatorRef: 'filterActivator' })
    },

    groupMenuOpen(isOpen) {
      this.handleMenuOpenChange({ isOpen, popoverRef: 'groupPopover', activatorRef: 'groupActivator' })
    },

    sortMenuOpen(isOpen) {
      this.handleMenuOpenChange({ isOpen, popoverRef: 'sortPopover', activatorRef: 'sortActivator' })
    },

    columnsMenuOpen(isOpen) {
      this.handleMenuOpenChange({ isOpen, popoverRef: 'columnsPopover', activatorRef: 'columnsActivator' })
    },

    // Close all menus when route query changes
    '$route.query': {
      handler() {
        this.closeAllMenus()
      },
    },

    // Sync local grouping state with store (only when NOT self-managed)
    currentGroupByProperties: {
      immediate: true,
      handler(newVal) {
        if (this.selfManaged) return []

        this.localGroupedBy = [...(newVal || [])]

        return this.localGroupedBy
      },
    },

    // Sync local sorting state with store (only when NOT self-managed)
    sortByKeys: {
      immediate: true,
      handler(keys) {
        if (this.selfManaged) return []

        this.syncSortFromStore(keys)

        return this.localGroupedBy
      },
    },

    // Sync when sort directions change externally (e.g., from column header clicks)
    sortByDirections: {
      handler() {
        if (this.selfManaged) return []

        this.syncSortFromStore(this.sortByKeys)

        return this.localGroupedBy
      },
    },
  },
  methods: {
    getRefElement(refName) {
      const ref = this.$refs[refName]
      if (!ref) return null
      return ref.$el || ref
    },

    focusFirstInteractiveIn(refName) {
      const root = this.getRefElement(refName)
      if (!root) return

      const selector = [
        'button:not([disabled])',
        'input:not([disabled])',
        'textarea:not([disabled])',
        '[role="button"]',
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ')

      const target = root.querySelector(selector)
      if (target && typeof target.focus === 'function') {
        target.focus()
      }
    },

    focusActivator(refName) {
      this.$nextTick(() => {
        const target = this.getRefElement(refName)
        if (target && typeof target.focus === 'function') {
          target.focus()
        }
      })
    },

    handleMenuOpenChange({ isOpen, popoverRef, activatorRef }) {
      if (isOpen) {
        this.$nextTick(() => {
          this.focusFirstInteractiveIn(popoverRef)
        })
        return
      }

      this.focusActivator(activatorRef)
    },

    closeMenuAndRestoreFocus(menuName) {
      const menuMap = {
        columns: {
          activatorRef: 'columnsActivator',
          model: 'columnsMenuOpen',
        },
        filter: {
          activatorRef: 'filterActivator',
          model: 'filterMenuOpen',
        },
        group: {
          activatorRef: 'groupActivator',
          model: 'groupMenuOpen',
        },
        sort: {
          activatorRef: 'sortActivator',
          model: 'sortMenuOpen',
        },
      }

      const item = menuMap[menuName]
      if (!item) return

      this[item.model] = false
      this.focusActivator(item.activatorRef)
    },

    getGroupOptions(index) {
      const otherSelected = [
        ...this.localGroupedBy.slice(0, index),
        ...this.localGroupedBy.slice(index + 1),
      ].map((key) => ({
        text: this.groupOptionLabel(key),
        value: key,
      }))
      return [...otherSelected, ...this.availableGroupOptions]
    },
    /**
     * Sync local sorting state from store
     *
     * @param {Array<string>} keys - The sort keys from the store
     * @returns {Array<{ column: string | null, direction: 'asc' | 'desc' }>} The local sort state
     */
    syncSortFromStore(keys) {
      // If there are no sort keys, initialize with a default sort row.
      if (!keys || keys.length === 0) {
        // Set a default sort row with no column selected and ascending direction.
        this.localSorts = [{ column: null, direction: 'asc' }]

        // Return early since we have initialized the default sort row.
        return this.localSorts
      }

      const directions = this.sortByDirections || []

      this.localSorts = keys.map((key, i) => ({
        column: key || null,
        direction: directions[i] === true ? 'desc' : 'asc',
      }))

      return this.localSorts
    },
    /** Close all popover menus */
    closeAllMenus() {
      this.filterMenuOpen = false
      this.groupMenuOpen = false
      this.sortMenuOpen = false
      this.columnsMenuOpen = false
    },

    // ============ FILTER METHODS ============
    async applyFilters() {
      const { reportId, reportKey, reportName, subId } = this
      const report = { reportId, reportKey, reportName, subId }

      if (this.isSelfManaged) {
        const filterData = this.currentFilterData || {}
        this.$emit('on-apply-filters', { filterData, reportKey })
        this.filterMenuOpen = false
        return
      }

      this.$store.commit('tools/isCurrentlyGrouping', true)
      this.$store.commit('tools/isCurrentlyMerging', true)

      render.ensureViewIsUpdatedAndRenderedImmediately(async () => {
        await this.$store.dispatch('tools/applyFilters', report)
        this.$emit('on-apply-filters')
        this.filterMenuOpen = false
      })
    },

    async resetFilters() {
      const { reportKey } = this

      if (this.isSelfManaged) {
        const defaultFilters = this.filterDefaults?.[reportKey] || {}
        this.$emit('on-reset-filters', { filterData: defaultFilters, reportKey })
        await this.$store.dispatch('tools/resetFilters')
        this.filterMenuOpen = false
        return
      }

      render.ensureViewIsUpdatedAndRenderedImmediately(async () => {
        await this.$store.dispatch('tools/resetFilters')
        this.$emit('on-reset-filters')
        this.filterMenuOpen = false
      })
    },

    clearActiveFilter() {
      this.$emit('clear-active-filter')
    },

    // ============ GROUPING METHODS ============
    toggleGroupSelectAll() {
      if (this.hasGroupSelectedAll) {
        this.localGroupedBy = []
      } else {
        this.localGroupedBy = this.resolvedGroupingOptions.map((opt) => opt.value)
      }
    },

    toggleGroupOption(item) {
      const { value } = item
      const idx = this.localGroupedBy.indexOf(value)
      if (idx > -1) {
        this.localGroupedBy.splice(idx, 1)
      } else {
        this.localGroupedBy.push(value)
      }
    },

    clearGrouping() {
      this.localGroupedBy = []
      if (!this.selfManaged) {
        this.$store.dispatch('tools/clearGroupBy')
      }
      this.$emit('on-grouping-change', [])
      this.groupMenuOpen = false
    },

    groupOptionLabel(groupKey) {
      const option = this.resolvedGroupingOptions.find((o) => o.value === groupKey)
      return option ? option.text : groupKey
    },

    removeGroupRow(index) {
      this.localGroupedBy.splice(index, 1)
    },

    onGroupColumnSelected(value) {
      if (value && !this.localGroupedBy.includes(value)) {
        this.localGroupedBy.push(value)
      }
      this.$nextTick(() => {
        this.pendingGroupColumn = null
      })
    },

    /**
     * Replace the group column at the given index with a new value
     * @param {number} index - The index of the group row to edit
     * @param {string} value - The new group column value
     */
    saveEditedGroupRow(index, value) {
      if (value && value !== this.localGroupedBy[index]) {
        // Replace the group at this index with the new value
        this.$set(this.localGroupedBy, index, value)
      }
      this.editingGroupRowIndex = null
    },

    applyGrouping() {
      if (!this.selfManaged) {
        this.$store.dispatch('tools/setGroupBy', this.localGroupedBy)
      }
      this.$emit('on-grouping-change', this.localGroupedBy)
      this.groupMenuOpen = false
    },

    // ============ SORTING METHODS ============
    sortRowLabel(index) {
      return index === 0 ? 'Sort By' : 'Then By'
    },

    /**
     * @method availableSortColumns
     * @desc Return the list of column keys available for sorting.
     * @param index {Number} The index of the sort input.
     * @returns {Array<Object>} The list of available sortable columns.

     */
    availableSortColumns(index) {
      // Get array of column keys that are already used in other sort rows.
      // Exclude the column at the specified index to avoid conflicts with
      // the current sort input.
      const usedColumns = this.localSorts
        .filter((s, i) => i !== index && s.column)
        .map((s) => s.column)

      // Return sortable columns that are not already selected for sorting.
      // These values populate the dropdowns for the sort input at the given index.
      return this.sortableColumns.filter((col) => !usedColumns.includes(col.value))
    },

    toggleSortDirection(index) {
      const sort = this.localSorts[index]
      this.$set(this.localSorts, index, {
        ...sort,
        direction: sort.direction === 'asc' ? 'desc' : 'asc',
      })
    },

    addSortRow() {
      if (this.localSorts.length < this.maxSortLevel) {
        // Add a new sort row with default values.
        // This determines the default values for a new sort row.
        this.localSorts.push({ column: null, direction: 'asc' })
      }
    },

    removeSortRow(index) {
      this.localSorts.splice(index, 1)
      if (this.localSorts.length === 0) {
        this.localSorts = [{ column: null, direction: 'asc' }]
      }
    },

    clearSorting() {
      this.localSorts = [{ column: null, direction: 'asc' }]
      if (!this.selfManaged) {
        this.$store.dispatch('tools/clearSortBy')
        this.$store.commit('tools/sortingByKeys', [])
        this.$store.commit('tools/sortingByDirections', [])
      }
      this.$emit('on-sorting-change', [])
      this.sortMenuOpen = false
    },

    applySorting() {
      const sorts = this.localSorts.filter((s) => s.column).map((s) => ({
        column: s.column,
        direction: s.direction,
      }))
      if (!this.selfManaged) {
        this.$store.dispatch('tools/setSortBy', sorts)
        const sortKeys = sorts.map((s) => s.column)
        const sortDirections = sorts.map((s) => s.direction === 'desc')
        this.$store.commit('tools/sortingByKeys', sortKeys)
        this.$store.commit('tools/sortingByDirections', sortDirections)
      }
      this.$emit('on-sorting-change', sorts)
      this.sortMenuOpen = false
    },

    // ============ COLUMN METHODS ============

    /**
     * @method freezeColumn
     * @desc Freezes or unfreezes the specified column in the table.
     * @param column {Object} The column to freeze or unfreeze.
     * @return {boolean} Returns true if the column was successfully frozen or unfrozen, false otherwise.
     * @see src/tools/store/actions.js#freezeColumn
     * @see src/tools/components/table/ColumnPicker.vue#freezeColumn
     */
    freezeColumn(column) {
      const { tableColumns, reportKey } = this
      const columnIdentifier = this.getColumnIdentifier(column)
      const columnIndex = tableColumns.findIndex(
        (tableColumn) => this.getColumnIdentifier(tableColumn) === columnIdentifier,
      )

      if (columnIndex === -1) {
        return false
      }

      if (this.selfManaged) {
        const newColumns = [...tableColumns]
        newColumns[columnIndex] = {
          ...newColumns[columnIndex],
          frozen: !newColumns[columnIndex].frozen,
        }
        this.$emit('on-columns-change', columnUtils.normalizeColumns(newColumns, this.currentRequiredColumns))
        return false
      }

      this.$store.dispatch('tools/freezeColumn', { columnIndex, reportKey, tableColumns })

      return true
    },

    getColumnLabel(column) {
      return columnUtils.getColumnLabel(column)
    },

    getColumnGroup(column) {
      return column?.group || 'Other'
    },

    shouldRenderColumnGroupHeader(index) {
      if (!this.hasMultipleColumnGroups) return false
      if (index === 0) return true

      const currentColumn = this.orderedColumns[index]
      const previousColumn = this.orderedColumns[index - 1]

      return this.getColumnGroup(currentColumn) !== this.getColumnGroup(previousColumn)
    },

    getColumnIdentifier(column) {
      return columnUtils.getColumnKey(column)
    },

    toggleColumn(column) {
      const { currentRequiredColumns, tableColumns, reportKey } = this
      const columnIdentifier = this.getColumnIdentifier(column)
      const findColumnByIdentifier = (col) => this.getColumnIdentifier(col) === columnIdentifier
      const columnIndex = tableColumns.findIndex(findColumnByIdentifier)

      if (columnIndex === -1) return

      const newColumns = [...tableColumns]
      const selectedColumn = newColumns[columnIndex]
      const isRequiredColumn = currentRequiredColumns?.length
        && currentRequiredColumns.includes(this.getColumnIdentifier(selectedColumn))

      if (isRequiredColumn) {
        const message = 'Cannot Hide Required Column'
        this.$store.commit('tools/snackbar', true)
        this.$store.commit('tools/snack', { message, status: 'error' })
        return
      }

      newColumns[columnIndex] = {
        ...selectedColumn,
        visible: selectedColumn.visible === false,
      }

      if (this.selfManaged) {
        this.$emit('on-columns-change', columnUtils.normalizeColumns(newColumns, this.currentRequiredColumns))
        return
      }
      this.$store.commit('tools/columns', { [reportKey]: newColumns })
    },

  },
}
</script>

<style scoped lang="scss">
.tools-toolbar {
  gap: 6px;
}

.toolbar-btn {
  white-space: nowrap;
}

.tools-popover {
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
}

.tools-popover-header {
  flex-shrink: 0;
  background-color: #fafafa;
}

.theme--dark .tools-popover-header {
  background-color: #2d2d2d;
}

.tools-popover-content {
  overflow-y: auto;
  flex: 1 1 auto;
  max-height: calc(100vh - 340px);
}

.columns-panel {
  .columns-list {
    max-height: calc(100vh - 380px);
    overflow-y: auto;
  }

  .column-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }
}

.theme--dark .columns-panel {
  .column-item {
    border-bottom-color: rgba(255, 255, 255, 0.06);

    &:hover {
      background-color: rgba(255, 255, 255, 0.02);
    }
  }
}

.sort-row {
  transition: background-color 0.15s ease;
}

.sort-dir-btn {
  min-width: 72px;
}

.remove-sort-btn:hover {
  color: #d82727;

  .grey--text {
    color: #d82727 !important;
  }
}

.group-row {
  background-color: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: background-color 0.15s ease;
}

.group-row:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.group-edit-icon {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.group-label-row:hover .group-edit-icon {
  opacity: 0.6;
}

.group-label-row {
  padding: 2px 4px;
  border-radius: 4px;
  min-height: 28px;
}

.group-label-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.theme--dark .group-label-row:hover {
  background-color: rgba(255, 255, 255, 0.06);
}

.theme--dark .group-row {
  background-color: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .group-row:hover {
  background-color: rgba(255, 255, 255, 0.07);
}

.cursor-grab {
  cursor: grab;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
